/**
 * Core service function for retrieving and consolidating thing fault/dtc events.
 * Handles pagination, normalization, grouping, episode derivation, filtering, and sorting.
 */

import { ConsolidatedFault, FaultEpisode, FaultEventData, FaultGroupKey, FaultStatusValue, FaultSummaryTotals, GetThingFaultSummaryInput, GetThingFaultSummaryResult, RawFaultEvent, WarningValue } from '../types/thing-fault-summary';

import { GenericLampState } from '@eagle/data-function-types';
import type { EthingsRestClient } from '@eagle/ethings-rest-client';

const DEFAULT_RANGE_DAYS = 30;

export const isLampLit = (value: unknown): boolean => {
  return value === true
    || value === GenericLampState.ON
    || value === GenericLampState.SLOW_FLASH
    || value === GenericLampState.FAST_FLASH;
};

/**
 * Validate input parameters.
 * Throws an error if validation fails.
 */
export const validateInput = (input: GetThingFaultSummaryInput): void => {
  if (!input.thingId || input.thingId.trim() === '') {
    throw new Error('thingId is required');
  }

  const range = input.range ?? DEFAULT_RANGE_DAYS;
  if (!Number.isInteger(range) || range < 1) {
    throw new Error('range must be an integer >= 1 (days)');
  }

  if (input.status !== undefined && !Object.values(FaultStatusValue).includes(input.status)) {
    throw new Error(`status must be one of: ${Object.values(FaultStatusValue).join(', ')}`);
  }

  if (input.warning !== undefined && !Object.values(WarningValue).includes(input.warning)) {
    throw new Error(`warning must be one of: ${Object.values(WarningValue).join(', ')}`);
  }
};

/**
 * Calculate the date range for the query.
 * Returns ISO 8601 date strings for start and end.
 */
export const calculateDateRange = (
  rangeDays: number,
  now: Date = new Date(),
): { start: string; end: string } => {
  const end = new Date(now);
  end.setUTCHours(23, 59, 59, 999);

  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - rangeDays);
  start.setUTCHours(0, 0, 0, 0);

  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
};

/**
 * Build a deterministic group key for a fault.
 * Key format: "{thingId}#{protocol}#{sourceAddress}#{code}#{fmi}"
 */
export const buildFaultGroupKey = (
  thingId: string,
  protocol: string,
  sourceAddress: string,
  code: string,
  fmi: string,
): FaultGroupKey => {
  return `${thingId}#${protocol}#${sourceAddress}#${code}#${fmi}`;
};

/**
 * Normalize an event's fault data.
 * Extracts the fault information from the event.data structure.
 */
export const normalizeFaultData = (event: RawFaultEvent): FaultEventData => {
  const data = event.data;
  return {
    active: data.active ?? false,
    code: data.code ?? '',
    failureModeIdentifier: data.failureModeIdentifier ?? '',
    protocol: data.protocol ?? '',
    sourceAddress: data.sourceAddress ?? '',
    occurrenceCount: data.occurrenceCount,
    redStop: data.redStop,
    protect: data.protect,
    amberWarning: data.amberWarning,
  };
};

/**
 * Derive warning level from a fault's data.
 * Priority: red-stop > protect > amber > none
 */
export const deriveWarning = (faultEvents: FaultEventData[]): WarningValue => {
  for (const event of faultEvents) {
    if (isLampLit(event.redStop)) return WarningValue.RedStop;
  }

  for (const event of faultEvents) {
    if (isLampLit(event.protect)) return WarningValue.Protect;
  }

  for (const event of faultEvents) {
    if (isLampLit(event.amberWarning)) return WarningValue.Amber;
  }

  return WarningValue.None;
};

/**
 * Episode lifecycle state machine.
 * Takes sorted events (oldest first) and derives episodes.
 *
 * Rules:
 * - `true` (active) opens an episode
 * - Consecutive `true` values do not create additional episodes
 * - `false` (inactive) closes the current episode
 * - Consecutive `false` values are ignored
 * - Leading `false` becomes `activeAtRangeStart=true` (started before range)
 * - Trailing `true` has `activeAtRangeEnd=true` (still ongoing)
 */
export const deriveEpisodes = (
  sortedEvents: (RawFaultEvent & { normalized: FaultEventData })[],
): FaultEpisode[] => {
  const episodes: FaultEpisode[] = [];
  let currentEpisode: FaultEpisode | null = null;
  let previousActive: boolean | null = null;

  for (const event of sortedEvents) {
    const { active } = event.normalized;

    // Transition: inactive → active (open new episode)
    if (active && !previousActive) {
      if (currentEpisode) {
        // Should not happen in normal flow
        episodes.push(currentEpisode);
      }
      currentEpisode = {
        startOccurred: event.occurred,
        activeAtRangeStart: false,
        activeAtRangeEnd: true, // Assume true until proven otherwise
        count: 1,
      };
    }
    // Transition: active → inactive (close episode)
    else if (!active && previousActive) {
      if (currentEpisode) {
        currentEpisode.endOccurred = event.occurred;
        currentEpisode.activeAtRangeEnd = false;
        episodes.push(currentEpisode);
        currentEpisode = null;
      }
    }
    // Active → Active (continue episode)
    else if (active && previousActive && currentEpisode) {
      currentEpisode.count += 1;
    }
    // Inactive → Inactive (ignore, but mark as started-before-range if first event)
    else if (!active && !previousActive && previousActive === null) {
      // First event is inactive → fault was active before the range
      // We'll mark this by setting a flag when we open the first actual episode
      // Or handle it separately
    }

    previousActive = active;
  }

  // Handle trailing active episode
  if (currentEpisode) {
    currentEpisode.activeAtRangeEnd = true;
    episodes.push(currentEpisode);
  }

  // Handle leading inactive: if first event is inactive, mark the first (and only) episode as started-before-range
  if (sortedEvents.length > 0 && !sortedEvents[0].normalized.active && episodes.length > 0) {
    episodes[0].activeAtRangeStart = true;
  }

  return episodes;
};

/**
 * Derive fault status from episodes.
 * - 'active': At least one episode is active at range end
 * - 'cleared': All episodes are closed
 * - 'unknown': No episodes
 */
export const deriveFaultStatus = (episodes: FaultEpisode[]): FaultStatusValue => {
  if (episodes.length === 0) {
    return FaultStatusValue.Unknown;
  }

  // Check if any episode is active at range end
  for (const episode of episodes) {
    if (episode.activeAtRangeEnd) {
      return FaultStatusValue.Active;
    }
  }

  return FaultStatusValue.Cleared;
};

/**
 * Apply status and warning filters to faults.
 * Returns only faults matching both filters (if specified).
 */
export const applyFilters = (
  faults: ConsolidatedFault[],
  input: GetThingFaultSummaryInput,
): ConsolidatedFault[] => {
  return faults.filter((fault) => {
    if (input.status && fault.status !== input.status) {
      return false;
    }
    if (input.warning && fault.warning !== input.warning) {
      return false;
    }
    return true;
  });
};

/**
 * Sort faults according to priority.
 * Order: status (active > cleared > unknown) → warning priority → most recent → code
 */
export const sortFaults = (faults: ConsolidatedFault[]): ConsolidatedFault[] => {
  const statusPriority: Record<FaultStatusValue, number> = {
    [FaultStatusValue.Active]: 0,
    [FaultStatusValue.Cleared]: 1,
    [FaultStatusValue.Unknown]: 2,
  };

  const warningPriority: Record<WarningValue, number> = {
    [WarningValue.RedStop]: 0,
    [WarningValue.Protect]: 1,
    [WarningValue.Amber]: 2,
    [WarningValue.None]: 3,
    [WarningValue.Unknown]: 4,
  };

  return [...faults].sort((a, b) => {
    // 1. Status priority
    const statusCmp = statusPriority[a.status] - statusPriority[b.status];
    if (statusCmp !== 0) return statusCmp;

    // 2. Warning priority
    const warningCmp = warningPriority[a.warning] - warningPriority[b.warning];
    if (warningCmp !== 0) return warningCmp;

    // 3. Most recent occurrence (descending)
    const lastCmp = new Date(b.timing.lastOccurred).getTime() - new Date(a.timing.lastOccurred).getTime();
    if (lastCmp !== 0) return lastCmp;

    // 4. Code (ascending)
    return a.code.localeCompare(b.code);
  });
};

/**
 * Build summary totals from consolidated faults.
 */
export const buildSummaryTotals = (
  faults: ConsolidatedFault[],
  totalEventCount: number,
): FaultSummaryTotals => {
  const totals: FaultSummaryTotals = {
    totalFaults: faults.length,
    activeCount: 0,
    clearedCount: 0,
    unknownCount: 0,
    warningCounts: {
      [WarningValue.RedStop]: 0,
      [WarningValue.Protect]: 0,
      [WarningValue.Amber]: 0,
      [WarningValue.None]: 0,
      [WarningValue.Unknown]: 0,
    },
    totalEventCount,
  };

  for (const fault of faults) {
    if (fault.status === FaultStatusValue.Active) totals.activeCount += 1;
    else if (fault.status === FaultStatusValue.Cleared) totals.clearedCount += 1;
    else totals.unknownCount += 1;

    totals.warningCounts[fault.warning] += 1;
  }

  return totals;
};

/**
 * Main service function: fetch and consolidate fault/dtc events for a thing.
 */
export const getThingFaultSummary = async (
  restClient: EthingsRestClient,
  thingId: string,
  range?: number,
  status?: FaultStatusValue,
  warning?: WarningValue,
): Promise<GetThingFaultSummaryResult> => {
  const input: GetThingFaultSummaryInput = { thingId, range, status, warning };
  const startTime = Date.now();

  // Validate input
  validateInput(input);

  const rangeDays = range ?? DEFAULT_RANGE_DAYS;
  const { start: rangeStart, end: rangeEnd } = calculateDateRange(rangeDays);

  // Fetch all events
  const allEvents: RawFaultEvent[] = [];
  let pagesFetched = 0;

  // Note: getWithThing handles pagination internally; we fetch all events in one call
  try {
    const response = await restClient.thingEvent.getWithThing(input.thingId, {
      dateRangeStart: new Date(rangeStart),
      dateRangeFinish: new Date(rangeEnd),
      limit: 100,
      filter: {
        eventTypeId: 'update',
        feature: 'fault/dtc',
      } as unknown as Record<string, unknown>,
    });

    allEvents.push(...(response.items?.map((item) => item as unknown as RawFaultEvent) ?? []));
    pagesFetched = 1;
  } catch (error) {
    // Log but continue; return what we have
    console.error(`Error fetching thing-event data for ${input.thingId}:`, error);
  }

  // Sort events by occurred (oldest first)
  allEvents.sort((a, b) => new Date(a.occurred).getTime() - new Date(b.occurred).getTime());

  // Normalize events
  const normalizedEvents = allEvents.map((event) => ({
    ...event,
    normalized: normalizeFaultData(event),
  }));

  // Group events by fault identity
  const faultGroups = new Map<FaultGroupKey, (typeof normalizedEvents)[0][]>();
  for (const event of normalizedEvents) {
    const key = buildFaultGroupKey(
      event.thingId,
      event.normalized.protocol,
      event.normalized.sourceAddress,
      event.normalized.code,
      event.normalized.failureModeIdentifier,
    );

    if (!faultGroups.has(key)) {
      faultGroups.set(key, []);
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    faultGroups.get(key)!.push(event);
  }

  // Build consolidated faults
  const consolidatedFaults: ConsolidatedFault[] = [];
  const rangeStartDate = new Date(rangeStart);
  const rangeEndDate = new Date(rangeEnd);

  for (const [groupKey, events] of faultGroups) {
    const firstEvent = events[0];
    const lastEvent = events[events.length - 1];
    const normalizedDataList = events.map((e) => e.normalized);

    // Derive episodes
    const episodes = deriveEpisodes(events);

    // Derive status and warning
    const status = deriveFaultStatus(episodes);
    const warning = deriveWarning(normalizedDataList);

    // Calculate timing
    const firstOccurred = new Date(firstEvent.occurred);
    const lastOccurred = new Date(lastEvent.occurred);
    const daysSinceFirst = Math.floor((rangeEndDate.getTime() - firstOccurred.getTime()) / (1000 * 60 * 60 * 24));
    const daysSinceLast = Math.floor((rangeEndDate.getTime() - lastOccurred.getTime()) / (1000 * 60 * 60 * 24));

    // Calculate behaviour
    const rangeSpanDays = (rangeEndDate.getTime() - rangeStartDate.getTime()) / (1000 * 60 * 60 * 24);
    const eventsPerDay = events.length / Math.max(rangeSpanDays, 1);
    const becameActiveDuringRange =
      episodes.length > 0 && !episodes[0].activeAtRangeStart && new Date(episodes[0].startOccurred) >= rangeStartDate;
    const clearedDuringRange = episodes.some((ep) => ep.endOccurred && new Date(ep.endOccurred) <= rangeEndDate);

    const activeCount = events.filter((e) => e.normalized.active).length;
    const inactiveCount = events.length - activeCount;

    const fault: ConsolidatedFault = {
      id: groupKey,
      thingId: firstEvent.thingId,
      protocol: firstEvent.normalized.protocol,
      sourceAddress: firstEvent.normalized.sourceAddress,
      code: firstEvent.normalized.code,
      failureModeIdentifier: firstEvent.normalized.failureModeIdentifier,
      status,
      warning,
      timing: {
        firstOccurred: firstEvent.occurred,
        lastOccurred: lastEvent.occurred,
        daysSinceFirst,
        daysSinceLast,
      },
      counts: {
        eventCount: events.length,
        activeCount,
        inactiveCount,
        episodeCount: episodes.length,
      },
      behaviour: {
        eventsPerDay,
        becameActiveDuringRange,
        clearedDuringRange,
      },
      latestEvent: {
        occurred: lastEvent.occurred,
        active: lastEvent.normalized.active,
        data: lastEvent.data as Record<string, unknown>,
      },
      episodes,
    };

    consolidatedFaults.push(fault);
  }

  // Apply filters
  const filteredFaults = applyFilters(consolidatedFaults, input);

  // Sort
  const sortedFaults = sortFaults(filteredFaults);

  // Build result
  const result: GetThingFaultSummaryResult = {
    range: {
      rangeStart,
      rangeEnd,
      daysRequested: rangeDays,
    },
    totals: buildSummaryTotals(sortedFaults, allEvents.length),
    faults: sortedFaults,
    meta: {
      eventCount: allEvents.length,
      pagesFetched,
      processingTimeMs: Date.now() - startTime,
    },
  };

  return result;
};
